import { Component, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { NbMenuItem, NbMenuService, NbSidebarService } from "@nebular/theme";
import { filter, Subscription } from "rxjs";
import { AuthService } from "../services/auth.service";
import { UserCompany } from "../models/user-company.model";
import { environment } from "src/environments/environment";
import { NbDialogService, NbToastrService } from "@nebular/theme";
import { LogoutComponent } from "./logout/logout.component";
import { CompanySelectorDialogComponent } from "./company-selector-dialog/company-selector-dialog.component";
import { CompanyContextService } from "../services/company-context.service";
import { CrmReminderService } from "../services/crm-reminder.service";
import { User } from "src/app/models/user.model";
import {
  ANIMAL_TYPE_IDS,
  ANIMAL_TYPE_LABELS,
  ANIMAL_TYPE_ICONS,
} from "../constants/animal-types.constants";

@Component({
    selector: "app-page",
    templateUrl: "page.component.html",
    styleUrls: ["page.component.scss"],
    standalone: false
})
export class PageComponent implements OnDestroy {
  public menu: NbMenuItem[] = [];
  /** Entrée de déconnexion du menu latéral, affichée sur téléphone seulement. */
  public readonly logoutMenu: NbMenuItem[] = [
    { title: "Déconnexion", icon: { icon: "q-logout", pack: "custom" } },
  ];
  public company: UserCompany | null;
  public companyName: string | null = null;
  public uploadBaseUrl: string = environment.uploadBaseUrl;
  public isAdmin: boolean = false;
  public animalTypeOptions: Array<{ id: number; label: string }> = [];
  public selectedAnimalTypeId: number | null = null;
  public displayAnimalTypeSelector = false;
  public crmUrgentCount: number = 0;
  public alertsCount: number = 0;

  private companySubscription: Subscription | null = null;
  private crmReminderSubscription: Subscription | null = null;
  private alertsCountSubscription: Subscription | null = null;
  private logoutMenuSubscription: Subscription | null = null;
  private hoveredAnimalTypeId: number | null = null;
  private readonly selectableAnimalTypeIds = ANIMAL_TYPE_IDS;
  private readonly animalTypeLabels = ANIMAL_TYPE_LABELS;
  private readonly animalTypeIcons = ANIMAL_TYPE_ICONS;

  constructor(
    protected companyContextService: CompanyContextService,
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    protected authService: AuthService,
    protected crmReminderService: CrmReminderService,
    protected dialogService: NbDialogService,
    protected router: Router,
    protected toastrService: NbToastrService,
  ) {
    const user = this.authService.getUser();

    this.isAdmin = !!(user && user.isAdmin);

    // Démarrer le polling des notifications (rappels CRM pour les admins, alertes pour tous)
    this.crmReminderService.startPolling();

    if (this.isAdmin) {
      this.crmReminderSubscription = this.crmReminderService.urgentCount$.subscribe(count => {
        this.crmUrgentCount = count;
        this.buildMenu(); // Reconstruire le menu pour mettre à jour le badge
      });
    }

    this.alertsCountSubscription = this.crmReminderService.alertsCount$.subscribe(count => {
      this.alertsCount = count;
      if (!this.isAdmin) {
        this.initBreederMenu(this.authService.getUser());
      }
    });

    this.logoutMenuSubscription = this.menuService
      .onItemClick()
      .pipe(filter(({ tag }) => tag === "logout-menu"))
      .subscribe(({ item }) => {
        // L'entrée n'a pas de lien : elle ne doit pas rester surlignée si
        // l'utilisateur annule
        item.selected = false;
        this.logoutAction();
      });

    // Refresh user info on page load to ensure up-to-date data
    if (user && !user.isAdmin) {
      this.authService.refreshMe().subscribe();
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((ev: any) => {
        if (ev.url == "/") {
          if (user && user.isAdmin) {
            this.router.navigateByUrl("/companies");
          } else {
            this.router.navigateByUrl("/compare");
          }
        }
        //fermer la sidebar si on est sur un mobile, au changement de route
        if (window.innerWidth <= 1200 && window.innerWidth > 576) {
          this.sidebarService.compact("menu-sidebar");
        } else if (window.innerWidth <= 576) {
          this.sidebarService.collapse("menu-sidebar");
        }

        this.setActiveMenuItem(ev.urlAfterRedirects);
        this.buildMenu();
        this.updateDisplayAnimalTypeSelector(ev.urlAfterRedirects);
      });

    // Load initial company data
    const currentCompany = this.companyContextService.getCurrentCompany();
    if (currentCompany) {
      this.handleCompanyChange(currentCompany);
    }

    // Watch for company changes
    this.companySubscription = this.companyContextService
      .watchCompanyChanges()
      .subscribe((company) => {
        this.handleCompanyChange(company);
      });

    this.updateDisplayAnimalTypeSelector(this.router.url);
  }

  ngOnDestroy(): void {
    this.companySubscription?.unsubscribe();
    this.companySubscription = null;
    this.crmReminderSubscription?.unsubscribe();
    this.crmReminderSubscription = null;
    this.alertsCountSubscription?.unsubscribe();
    this.alertsCountSubscription = null;
    this.logoutMenuSubscription?.unsubscribe();
    this.logoutMenuSubscription = null;
    this.crmReminderService.stopPolling();
  }

  private buildMenu(): void {
    this.menu = [];
    const user = this.authService.getUser();
    if (user && user.isAdmin) {
      const companyString = localStorage.getItem("qwin-company");
      if (companyString) {
        const company = JSON.parse(companyString);
        this.companyName = company.name;
        this.initBreederMenu(user);
      }

      this.menu.push({
        title: "ADMINISTRATION",
      });
      this.menu.push({
        title: "Éleveurs",
        icon: "briefcase-outline",
        link: "/companies",
      });
      const crmJournalMenuItem: NbMenuItem = {
        title: "Journal d'appels",
        icon: "phone-outline",
        link: "/crm-journal",
      };

      // Ajouter le badge si des rappels urgents existent
      if (this.crmUrgentCount > 0) {
        crmJournalMenuItem.badge = {
          text: this.crmUrgentCount.toString(),
          status: 'danger',
        };
      }

      this.menu.push(crmJournalMenuItem);
      this.menu.push({
        title: "Animaux sans éleveur",
        icon: "search-outline",
        link: "/animals/search",
      });
      this.menu.push({
        title: "Configuration",
        icon: "file-text-outline",
        link: "/configuration",
      });
      this.menu.push({
        title: "Externes",
        icon: "code-outline",
        link: "/externs",
      });
      this.menu.push({
        title: "Timeline",
        icon: "clock-outline",
        link: "/timeline",
      });
      this.menu.push({
        title: "KPIs",
        icon: "bar-chart-outline",
        link: "/kpis",
      });
    } else {
      // Non-admin: show company name if available
      const currentCompany = user?.getCurrentCompany();
      this.companyName = currentCompany?.name || null;
      this.initBreederMenu(user);
    }
    this.setActiveMenuItem(this.router.url);
  }

  private initBreederMenu(user: User | null): void {
    this.menu = [];
    this.menu.push({
      title: "Accueil",
      icon: { icon: "q-accueil", pack: "custom" },
      link: "/compare",
    });
    this.menu.push({
      title: "Récap pesées",
      icon: { icon: "q-resultats", pack: "custom" },
      link: "/weighing-sessions",
    });
    if (user?.getCurrentCompany()) {
      const alertsItem: NbMenuItem = {
        title: "Alertes",
        icon: { icon: "q-alertes", pack: "custom" },
        link: "/alerts",
      };
      if (this.alertsCount > 0) {
        alertsItem.badge = { text: this.alertsCount.toString(), status: 'danger' };
      }
      this.menu.push(alertsItem);
      if (this.authService.hasInternalPermissions()) {
        this.menu.push({
          title: "Mon élevage",
          icon: { icon: "q-societe", pack: "custom" },
          link: "/settings",
        });
      }
    }
    this.setActiveMenuItem(this.router.url);
  }

  /**
   * Bascule l'état de la sidebar (utile sur mobile/tablette).
   * Retourne false pour empêcher la navigation du lien.
   */
  public toggleSidebar(): boolean {
    this.sidebarService.toggle(true, "menu-sidebar");
    return false;
  }

  /**
   * Ouvre la boîte de dialogue de déconnexion et déclenche le logout si confirmé.
   */
  public logoutAction(): void {
    this.dialogService.open(LogoutComponent).onClose.subscribe((response) => {
      if (response.validated) {
        this.authService.logout();
        this.router.navigateByUrl("/auth");
      }
    });
  }

  /**
   * Vérifie si l'utilisateur courant a plusieurs exploitations.
   */
  public hasMultipleCompanies(): boolean {
    const user = this.authService.getUser();
    return user?.hasMultipleCompanies() || false;
  }

  /**
   * Ouvre la boîte de dialogue de sélection d'exploitation.
   */
  public openCompanySelectorDialog(): void {
    this.dialogService
      .open(CompanySelectorDialogComponent, {
        dialogClass: "dialog-min-width",
      })
      .onClose.subscribe((response) => {
        if (response?.changed) {
          // Page reload is handled in the dialog component
        }
      });
  }

  public isAnimalTypeActive(typeId: number): boolean {
    return this.selectedAnimalTypeId === typeId;
  }

  /**
   * Déclenche la sélection d'un type d'animal côté serveur, sauf si déjà actif.
   */
  public selectAnimalType(typeId: number): void {
    if (!this.company || this.selectedAnimalTypeId === typeId) {
      return;
    }

    const currentUrl = this.router.url;
    const shouldRedirect = currentUrl.startsWith("/animals") || currentUrl.startsWith("/groups");

    this.applyAnimalType(typeId, true, shouldRedirect);
  }

  /**
   * Marque l'élément de menu correspondant à l'URL comme actif.
   */
  private setActiveMenuItem(url: string) {
    for (let item of this.menu) {
      item.selected = item.link === url;
      if (item.children) {
        for (let child of item.children) {
          child.selected = child.link === url;
        }
      }
    }
  }

  /**
   * Réagit à la mise à jour de la company (connexion, changement d'exploitation, refresh).
   * Alimente la liste de types disponibles et sélectionne celui indiqué par l'API.
   */
  private handleCompanyChange(company: UserCompany | null): void {
    this.company = company;
    this.crmReminderService.refreshCount();
    const availableTypeIds = (company?.animalTypeIds ?? []).filter((typeId) =>
      this.selectableAnimalTypeIds.includes(typeId),
    );
    this.animalTypeOptions = availableTypeIds.map((typeId) => ({
      id: typeId,
      label: this.animalTypeLabels[typeId] ?? `Type ${typeId}`,
    }));

    if (!company || this.animalTypeOptions.length === 0) {
      this.selectedAnimalTypeId = null;
      this.hoveredAnimalTypeId = null;
      this.authService.updateCachedAnimalType(null);
      return;
    }
    let initialTypeId: number | null = this.authService.getUser()?.currentAnimalTypeId ?? null;

    if (initialTypeId !== null && !availableTypeIds.includes(initialTypeId)) {
      initialTypeId = null;
    }

    if (initialTypeId === null && availableTypeIds.length > 0) {
      initialTypeId = availableTypeIds[0];
      this.applyAnimalType(initialTypeId, false);
    } else {
      this.selectedAnimalTypeId = initialTypeId;
      this.authService.updateCachedAnimalType(initialTypeId);
    }

    this.updateDisplayAnimalTypeSelector(this.router.url);
  }

  /**
   * Affiche ou masque la barre d'icônes selon la route courante et la présence d'options.
   */
  private updateDisplayAnimalTypeSelector(currentUrl: string): void {
    const breederRoutes = ["/compare", "/alerts", "/settings", "/animals", "/groups"];
    this.displayAnimalTypeSelector =
      !!this.company &&
      this.animalTypeOptions.length > 0 &&
      breederRoutes.some((route) => currentUrl.startsWith(route));
    if (!this.displayAnimalTypeSelector) {
      this.hoveredAnimalTypeId = null;
    }
  }

  /**
   * Retourne l'icône (on/off/hover) à afficher pour un type donné.
   */
  public getAnimalTypeIcon(typeId: number): string {
    const iconSet = this.animalTypeIcons[typeId];
    if (!iconSet) {
      return "";
    }
    if (this.isAnimalTypeActive(typeId)) {
      return iconSet.on;
    }
    if (this.hoveredAnimalTypeId === typeId) {
      return iconSet.hover;
    }
    return iconSet.off;
  }

  /**
   * Suit l'ID en survol pour basculer l'icône en mode hover.
   */
  public onAnimalTypeMouseEnter(typeId: number): void {
    this.hoveredAnimalTypeId = typeId;
  }

  /**
   * Nettoie l'état de survol quand la souris quitte une icône.
   */
  public onAnimalTypeMouseLeave(typeId: number): void {
    if (this.hoveredAnimalTypeId === typeId) {
      this.hoveredAnimalTypeId = null;
    }
  }

  /**
   * Applique le type sélectionné côté serveur, gère l'affichage et restaure l'état si échec.
   */
  private applyAnimalType(
    typeId: number,
    showError: boolean,
    redirectToDashboard: boolean = false,
  ): void {
    const previousType = this.selectedAnimalTypeId;
    this.selectedAnimalTypeId = typeId;

    this.authService.setAnimalType(typeId).subscribe((result) => {
      if (result) {
        this.authService.updateCachedAnimalType(typeId);
        this.companyContextService.notifyAnimalTypeChanged(typeId);

        if (redirectToDashboard) {
          this.router.navigateByUrl("/compare");
        }
      } else {
        if (showError) {
          this.toastrService.show(
            "",
            `Impossible de sélectionner ce type d'animal pour le moment`,
            { status: "danger" },
          );
        }
        this.selectedAnimalTypeId = previousType;
      }
    });
  }
}
